import { FC } from 'react';
import {
  ActionsMenu,
  ExternalLink,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import { useSearchParams } from 'utils/navigation';
import {
  adminShopVouchersFiltersSchema,
  useAdminShopVouchers,
  useExportXlsTemplate,
  useBulkVouchersUpdate,
  useBulkVouchersDelete,
} from 'store/admin-shop-vouchers';
import {
  DateRangeFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { useTranslation } from 'react-i18next';
import { useRouteParams } from 'utils/useIdParam';
import { APP, ROUTES } from 'config';
import { useModalContext } from 'store/modal';
import moment from 'moment';
import { formatCurrency } from 'utils/format';
import { usePredefinedToasts } from 'utils/Toast';
import { useAdminShopItem } from 'store/admin-shop';
import { getSinglePriceLabel } from 'components/marketplace/ItemCard/common/getPriceLabel';
import { COLOR_BY_STATUS, voucherStatuses } from 'store/vouchers';
import { useConfirm } from 'store/confirm';
import {
  DateSelectModal,
  EditFileVoucherModal,
  EditVoucherModal,
  ImportVoucherCodesModal,
  UploadVoucherFilesModal,
} from './atoms';

export const AdminShopVouchersList: FC = () => {
  const { t, i18n } = useTranslation();
  const { id: shopItemId } = useRouteParams(
    ROUTES.admin.shopItems.details.vouchers,
  );
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();
  const { searchParams: filters, navigate } = useSearchParams(
    adminShopVouchersFiltersSchema,
  );
  const { confirmation } = useConfirm();

  const { data: vouchers, isLoading } = useAdminShopVouchers(
    shopItemId,
    filters,
  );
  const { data: shopItem } = useAdminShopItem(shopItemId);

  const { mutate: exportXlsTemplate, isLoading: isExportingXlsTemplate } =
    useExportXlsTemplate();
  const { mutate: bulkUpdate, isLoading: isUpdating } = useBulkVouchersUpdate();
  const { mutate: bulkDelete, isLoading: isDeleting } = useBulkVouchersDelete();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <ListTotal total={vouchers?.total} />

          <Stack direction="row" alignItems="center" gap={1}>
            <Filters
              value={filters}
              initialValues={adminShopVouchersFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('common.status')}
                    value={value.status}
                    initialValue={initialValues.status}
                    onChange={(v) => setFilter('status', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={voucherStatuses.map((status) => ({
                          key: status,
                          label: t(`status.${status}`),
                        }))}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('marketplace.ordersTablePrice')}
                    value={value.prices}
                    initialValue={initialValues.prices}
                    onChange={(v) => setFilter('prices', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={
                          shopItem?.prices
                            .filter((price) => price.priceType === 'fixedPrice')
                            .map((price) => ({
                              label: getSinglePriceLabel(
                                price,
                                shopItem.currency,
                              ),
                              key: price.id,
                            })) ?? []
                        }
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('marketplaceOrders.orderDate')}
                    value={value.createdAt}
                    initialValue={initialValues.createdAt}
                    onChange={(v) => setFilter('createdAt', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <DateRangeFilter
                        value={value}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onCancel={close}
                        withTimezone
                      />
                    )}
                  </FilterButton>

                  <OpenSearchFilter
                    value={filters.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    placeholder={t('common.search')}
                    hint={t('shop.vouchers.search.hint')}
                  />
                </>
              )}
            </Filters>

            <Divider orientation="vertical" variant="middle" flexItem />

            <ActionsMenu
              items={[
                {
                  label: t('global.actions.upload_filetypes', {
                    fileTypes: ['.pdf', '.png'].join(', '),
                  }),
                  onClick: () => {
                    openModal({
                      component: UploadVoucherFilesModal,
                      props: { shopItemId },
                    });
                  },
                },
                {
                  label: t('shop.vouchers.actions.upload_codes'),
                  onClick: () => {
                    openModal({
                      component: ImportVoucherCodesModal,
                      props: {
                        shopItemId,
                      },
                    });
                  },
                },
                {
                  label: t('global.actions.export_template'),
                  onClick: () => {
                    exportXlsTemplate();
                  },
                  disabled: isExportingXlsTemplate,
                },
              ]}
            />
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        isLoading={isLoading}
        data={vouchers?.items ?? []}
        columns={[
          {
            id: 'id',
            header: t('integrations.hiBobServiceUserId'),
            cell: ({ row: { original: voucher } }) => voucher.sequence,
          },
          {
            id: 'creationDate',
            header: t('marketplaceOrders.orderDate'),
            cell: ({ row: { original: voucher } }) =>
              moment(voucher.createdAt).format('l LT'),
            meta: { sort: true },
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: voucher } }) => (
              <Typography color={COLOR_BY_STATUS[voucher.status]}>
                {t(`status.${voucher.status}`)}
              </Typography>
            ),
          },
          {
            id: 'price',
            header: t('marketplace.ordersTablePrice'),
            cell: ({ row: { original: voucher } }) =>
              formatCurrency(i18n.language, voucher.price, voucher.currency),
            meta: { align: 'right' },
          },
          {
            id: 'purchasePrice',
            header: t('shop.vouchers.purchasePrice.label'),
            cell: ({ row: { original: voucher } }) =>
              formatCurrency(
                i18n.language,
                voucher.purchasePrice,
                voucher.currency,
              ),
            meta: { align: 'right' },
          },
          {
            id: 'reference',
            header: t('shop.vouchers.purchaseReference.label'),
            cell: ({ row: { original: voucher } }) => voucher.purchaseReference,
          },
          {
            id: 'expiresAt',
            header: t('alternatives.employeeExpirationDate'),
            cell: ({ row: { original: voucher } }) =>
              voucher.expiresAt ? moment(voucher.expiresAt).format('l') : '',
          },
          {
            id: 'redeem',
            header: t('shop.vouchers.redeem.label'),
            cell: ({ row: { original: voucher } }) => (
              <Stack>
                {Object.entries(voucher.redeem).map(([type, value], i) => {
                  if (!value) return null;

                  return (
                    <Stack flexDirection="row" gap={0.5} key={i}>
                      <Typography noWrap>
                        {t(`shop.vouchers.redeem.${type}`)}:
                      </Typography>
                      {type === 'url' ? (
                        <ExternalLink
                          label={value.substring(0, 14) + '...'}
                          title={value}
                          href={value}
                        />
                      ) : (
                        <Typography variant="h3" noWrap>
                          {value}
                        </Typography>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            ),
          },
          {
            id: 'fileLink',
            header: t('shop.vouchers.fileLink.label'),
            cell: ({ row: { original: voucher } }) =>
              voucher.fileLink ? (
                <ExternalLink
                  label={voucher.fileLink.substring(0, 14) + '...'}
                  href={voucher.fileLink}
                />
              ) : null,
          },
          {
            id: 'orderId',
            header: t('marketplaceOrders.orderId'),
            cell: ({ row: { original: voucher } }) =>
              voucher.order ? (
                <Link
                  label={voucher.order.name}
                  to={ROUTES.admin.orders.details.replace(
                    ':id',
                    voucher.order.id,
                  )}
                />
              ) : null,
          },
          {
            id: 'lastAccess',
            header: t('order.last_access.label'),
            cell: ({ row: { original: voucher } }) =>
              voucher.lastAccess
                ? moment(voucher.lastAccess).format('l LT')
                : '',
          },
          {
            id: 'actions',
            cell: ({ row: { original: voucher } }) => {
              return (
                <ActionsMenu
                  type="compact"
                  items={[
                    {
                      label: t('common.edit'),
                      onClick: () => {
                        if (voucher.fileLink) {
                          openModal({
                            component: EditFileVoucherModal,
                            props: { shopItemId, voucherId: voucher.id },
                          });
                        } else {
                          openModal({
                            component: EditVoucherModal,
                            props: { shopItemId, voucherId: voucher.id },
                          });
                        }
                      },
                    },
                    ...(['archived', 'expired'] as const).map((status) => ({
                      label: t('global.actions.mark_as', {
                        status: t(`status.${status}`),
                      }),
                      onClick: () => {
                        bulkUpdate(
                          {
                            voucherIds: [voucher.id],
                            data: { status },
                          },
                          {
                            onSuccess: () => {
                              predefinedToasts.success.updated();
                            },
                          },
                        );
                      },
                      disabled: isUpdating,
                    })),
                    {
                      label: t('alternatives.employeeExpirationDate'),
                      onClick: async () => {
                        const response = await openModal({
                          component: DateSelectModal,
                        });

                        if (response.action === 'CONFIRM') {
                          bulkUpdate(
                            {
                              voucherIds: [voucher.id],
                              data: { expirationDate: response.date },
                            },
                            {
                              onSuccess: () => {
                                predefinedToasts.success.updated();
                              },
                            },
                          );
                        }
                      },
                      disabled: isUpdating,
                    },
                    {
                      label: t('actions.delete'),
                      onClick: async () => {
                        const { confirmed } = await confirmation(
                          t('common.areYouSureToDelete', {
                            title: voucher.sequence,
                          }),
                          undefined,
                          'danger',
                        );

                        if (confirmed) {
                          bulkDelete(
                            { voucherIds: [voucher.id] },
                            {
                              onSuccess: () => {
                                predefinedToasts.success.deleted();
                              },
                            },
                          );
                        }
                      },
                      disabled: isDeleting,
                      color: 'error',
                    },
                  ]}
                />
              );
            },
          },
        ]}
        pagination={
          vouchers && {
            count: vouchers.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          isLoading: isUpdating || isDeleting,
          actions: [
            ...(['archived', 'expired'] as const).map((status) => ({
              label: t('global.actions.mark_as', {
                status: t(`status.${status}`),
              }),
              onClick: () => {
                bulkUpdate(
                  { voucherIds: Object.keys(selectedRowIds), data: { status } },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            })),
            {
              label: t('alternatives.employeeExpirationDate'),
              onClick: async () => {
                const response = await openModal({
                  component: DateSelectModal,
                });

                if (response.action === 'CONFIRM') {
                  bulkUpdate(
                    {
                      voucherIds: Object.keys(selectedRowIds),
                      data: { expirationDate: response.date },
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        setSelectedRowIds({});
                      },
                    },
                  );
                }
              },
            },
            {
              label: t('actions.delete'),
              onClick: async () => {
                const { confirmed } = await confirmation(
                  t('common.areYouSureToDelete', {
                    title:
                      vouchers?.items
                        .filter((voucher) =>
                          Object.keys(selectedRowIds).includes(voucher.id),
                        )
                        .map((voucher) => voucher.sequence)
                        .join(', ') ?? t('shop.vouchers.title'),
                  }),
                  undefined,
                  'danger',
                );

                if (confirmed) {
                  bulkDelete(
                    { voucherIds: Object.keys(selectedRowIds) },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.deleted();
                        setSelectedRowIds({});
                      },
                    },
                  );
                }
              },
              color: 'error',
            },
          ],
        }}
      />
    </TableWrapper>
  );
};
